import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import InlineFeedback from './InlineFeedback';

const DEFAULT_WS_BASE_URL = `${
  window.location.protocol === 'https:' ? 'wss' : 'ws'
}://${window.location.hostname || '127.0.0.1'}:8000`;
const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL || DEFAULT_WS_BASE_URL;
const FETCH_LIMIT = 50;

const sortByNewest = (items) =>
  [...items].sort((a, b) => {
    const aTime = new Date(a.scheduled_at || a.sent_at || 0).getTime();
    const bTime = new Date(b.scheduled_at || b.sent_at || 0).getTime();
    return bTime - aTime;
  });

const normalizeNotification = (notification) => ({
  ...notification,
  is_read: Boolean(notification.is_read),
});

const countUnread = (items) => items.filter((item) => !item.is_read).length;

const formatDate = (value) => {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  const elapsedMs = Date.now() - dt.getTime();
  const elapsedMinutes = Math.floor(elapsedMs / 60000);

  if (elapsedMinutes < 0) {
    return dt.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (elapsedMinutes >= 0 && elapsedMinutes < 1) return 'Just now';
  if (elapsedMinutes >= 1 && elapsedMinutes < 60) return `${elapsedMinutes}m ago`;
  if (elapsedMinutes < 1440) return `${Math.floor(elapsedMinutes / 60)}h ago`;
  if (elapsedMinutes < 10080) return `${Math.floor(elapsedMinutes / 1440)}d ago`;

  return dt.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatAppointmentDateTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return '';
  const normalizedTime = String(timeValue).slice(0, 5);
  const dt = new Date(`${dateValue}T${normalizedTime}`);
  if (Number.isNaN(dt.getTime())) return `${dateValue} ${normalizedTime}`;
  return dt.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const splitReason = (message) => {
  const [summary, ...reasonParts] = String(message || '').split(/\s+Reason:\s+/i);
  return {
    summary: summary.trim().replace(/\.$/, ''),
    reason: reasonParts.join(' Reason: ').trim().replace(/\.$/, ''),
  };
};

const formatActorName = (value) => {
  const cleaned = String(value || '').trim().replace(/[_-]+/g, ' ');
  return cleaned ? `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}` : 'Someone';
};

const NOTIFICATION_TITLES = {
  reminder: { title: 'Appointment reminder', icon: '📅' },
  booking_canceled: { title: 'Booking canceled', icon: '✕' },
  booking_rejected: { title: 'Booking declined', icon: '✕' },
  booking_rescheduled: { title: 'Booking rescheduled', icon: '↻' },
  booking_reschedule_proposed: { title: 'New time proposed', icon: '🕒' },
  booking_reschedule_approved: { title: 'New time approved', icon: '✓' },
  booking_reschedule_rejected: { title: 'Proposed time declined', icon: '✕' },
  owner_message: { title: 'Message from your company', icon: '💬' },
};

const formatNotificationContent = (notification) => {
  const rawMessage = String(notification?.message || '').trim();
  const { summary, reason } = splitReason(rawMessage);
  const presentation = NOTIFICATION_TITLES[notification?.type] || {
    title: 'Notification',
    icon: '🔔',
  };
  let body = summary;
  let detail = '';
  let match;

  if (notification?.type === 'booking_rescheduled') {
    match = summary.match(/^Booking was rescheduled by (.+?) from (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(?::\d{2})? to (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(?::\d{2})?$/i);
    if (match) {
      body = `${formatActorName(match[1])} changed the appointment time.`;
      detail = `${formatAppointmentDateTime(match[2], match[3])} → ${formatAppointmentDateTime(match[4], match[5])}`;
    }
  } else if (['booking_canceled', 'booking_rejected'].includes(notification?.type)) {
    match = summary.match(/^Booking on (\d{4}-\d{2}-\d{2}) at (\d{2}:\d{2})(?::\d{2})? was (?:canceled|rejected) by (.+)$/i);
    if (match) {
      body = `${formatActorName(match[3])} ${notification.type === 'booking_rejected' ? 'declined' : 'canceled'} the appointment.`;
      detail = formatAppointmentDateTime(match[1], match[2]);
    }
  } else if (notification?.type === 'booking_reschedule_proposed') {
    match = summary.match(/^(.+?) proposed moving booking #\d+ to (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(?::\d{2})?$/i);
    if (match) {
      body = `${formatActorName(match[1])} suggested a new appointment time.`;
      detail = formatAppointmentDateTime(match[2], match[3]);
    }
  } else if (notification?.type === 'booking_reschedule_approved') {
    match = summary.match(/^(.+?) approved a proposed time for booking #\d+\. New time: (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(?::\d{2})?$/i);
    if (match) {
      body = `${formatActorName(match[1])} approved the new appointment time.`;
      detail = formatAppointmentDateTime(match[2], match[3]);
    }
  } else if (notification?.type === 'booking_reschedule_rejected') {
    match = summary.match(/^(.+?) rejected the proposed booking time for booking #\d+$/i);
    if (match) body = `${formatActorName(match[1])} declined the proposed appointment time.`;
  } else if (notification?.type === 'reminder') {
    match = summary.match(/^(?:Dear .+?, this is a reminder for your appointment|Dear .+?, you have an appointment scheduled) on (\d{4}-\d{2}-\d{2}) at (\d{2}:\d{2})(?::\d{2})?$/i);
    if (match) {
      body = 'You have an upcoming appointment.';
      detail = formatAppointmentDateTime(match[1], match[2]);
    }
  }

  return { ...presentation, body, detail, reason };
};

const channelLabel = (channel) => ({
  in_app: 'In app',
  email: 'Email',
  sms: 'SMS',
}[channel] || 'Update');

const NotificationCenter = ({
  setNotification,
  onUnreadCountChange,
  embedded = false,
  className = '',
}) => {
  const token = localStorage.getItem('token');
  const socketRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [panelFeedback, setPanelFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [onUnreadCountChange, unreadCount]);

  const pushPanelFeedback = (type, message) => setPanelFeedback({ type, message });

  const handleUpsertNotification = useCallback((incoming, prepend = false) => {
    const normalized = normalizeNotification(incoming);
    setNotifications((prev) => {
      const existingIdx = prev.findIndex((n) => n.id === normalized.id);
      let next;

      if (existingIdx >= 0) {
        next = [...prev];
        next[existingIdx] = { ...next[existingIdx], ...normalized };
      } else {
        next = prepend ? [normalized, ...prev] : [...prev, normalized];
      }

      const sorted = sortByNewest(next);
      setUnreadCount(countUnread(sorted));
      return sorted;
    });
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await api.get(`notifications/?limit=${FETCH_LIMIT}`);
      const results = (response.data?.results || []).map(normalizeNotification);
      const sorted = sortByNewest(results);

      setNotifications(sorted);
      setUnreadCount(response.data?.unread_count ?? countUnread(sorted));
      pushPanelFeedback('', '');
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      pushPanelFeedback('error', 'Could not load notifications right now.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchPreferences = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('notifications/preferences/');
      setPreferences(response.data);
      pushPanelFeedback('', '');
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      pushPanelFeedback('error', 'Could not load notification preferences.');
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      return;
    }

    fetchNotifications();
    fetchPreferences();
  }, [token, fetchNotifications, fetchPreferences]);

  useEffect(() => {
    if (!token) return undefined;

    const ws = new WebSocket(
      `${WS_BASE_URL}/ws/notifications/?token=${encodeURIComponent(token)}`
    );
    socketRef.current = ws;

    ws.onopen = () => {
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        const notification = payload?.notification;
        if (!notification) return;

        const shouldPrepend = payload.event === 'notification.created';
        handleUpsertNotification(notification, shouldPrepend);
      } catch (error) {
        console.error('Invalid websocket notification payload:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('Notification websocket error:', error);
    };

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (socketRef.current && socketRef.current.readyState <= WebSocket.OPEN) {
        socketRef.current.close();
      }
    };
  }, [token, handleUpsertNotification]);

  useEffect(() => {
    if (!isOpen) return undefined;

    panelRef.current?.focus();
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    const handleOutsideClick = (event) => {
      const target = event.target;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await api.post('notifications/mark-read/', { notification_ids: [notificationId] });
      setNotifications((prev) => {
        const next = prev.map((item) =>
          item.id === notificationId && !item.is_read
            ? { ...item, is_read: true, read_at: new Date().toISOString() }
            : item
        );
        setUnreadCount(countUnread(next));
        return next;
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      pushPanelFeedback('error', 'Failed to mark notification as read.');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('notifications/mark-read/', {});
      setNotifications((prev) => {
        const nowIso = new Date().toISOString();
        return prev.map((item) => ({
          ...item,
          is_read: true,
          read_at: item.read_at || nowIso,
        }));
      });
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      pushPanelFeedback('error', 'Failed to mark all notifications as read.');
    }
  };

  const updatePreference = async (key, value) => {
    if (!preferences) return;

    const previous = preferences;
    const optimistic = { ...preferences, [key]: value };
    setPreferences(optimistic);
    setIsSavingPreferences(true);

    try {
      const response = await api.patch('notifications/preferences/', { [key]: value });
      setPreferences(response.data);
      pushPanelFeedback('success', 'Preferences updated.');
      setNotification?.({ message: 'Notification preferences updated.', type: 'success' });
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      setPreferences(previous);
      pushPanelFeedback('error', 'Failed to update preferences.');
      setNotification?.({ message: 'Failed to update preferences.', type: 'error' });
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const hasNotifications = useMemo(() => notifications.length > 0, [notifications]);

  if (!token) return null;

  const wrapperClass = embedded
    ? `relative z-[1000] w-full sm:w-auto ${className}`
    : 'fixed bottom-4 right-4 z-[1000]';

  const panelClass = embedded
    ? 'fixed inset-0 z-[1100] flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:z-auto sm:mt-2 sm:h-auto sm:max-h-[75vh] sm:w-[min(22rem,calc(100vw-1rem))] sm:max-w-[95vw] sm:rounded-xl sm:border sm:border-gray-200 sm:p-4'
    : 'fixed inset-0 z-[1100] flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:absolute sm:inset-auto sm:bottom-full sm:right-0 sm:z-auto sm:mb-2 sm:h-auto sm:max-h-[75vh] sm:w-[min(22rem,calc(100vw-1rem))] sm:max-w-[95vw] sm:rounded-xl sm:border sm:border-gray-200 sm:p-4';

  return (
    <div className={wrapperClass}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="notification-center-panel"
        className="relative min-h-11 w-full rounded-full bg-[#6D3B7E] px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-[#5b3269] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] focus-visible:ring-offset-2 sm:w-auto"
      >
        🔔 Notifications
        {!isOpen && unreadCount > 0 && (
          <span
            className="absolute -right-2 -top-2 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#EF5350] px-1 text-xs font-semibold"
            aria-label={`${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          id="notification-center-panel"
          role="dialog"
          aria-label="Notification Center"
          tabIndex={-1}
          className={panelClass}
        >
          <div className="flex flex-none items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:mb-3 sm:border-0 sm:p-0">
            <h3 className="text-base font-semibold text-[#2E2E2E]">Notification Center</h3>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="text-xs text-[#6D3B7E] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
                onClick={fetchNotifications}
              >
                Refresh
              </button>
              <button
                type="button"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-2 py-1 text-xl text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] sm:min-h-0 sm:min-w-0 sm:rounded sm:text-xs"
                onClick={() => setIsOpen(false)}
                aria-label="Close notification center"
              >
                <span aria-hidden="true">✕</span>
                <span className="sr-only sm:not-sr-only">Close</span>
              </button>
            </div>
          </div>

          <div data-testid="notification-scroll-area" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-3 sm:-mr-1 sm:px-0 sm:pb-0 sm:pr-1 sm:pt-0">
            <InlineFeedback type={panelFeedback.type || 'info'} message={panelFeedback.message} className="mb-3" />

            <div className="mb-3 flex items-center justify-between text-sm">
              <p className="text-gray-600">{unreadCount} unread</p>
              <button
                type="button"
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
                className="min-h-10 rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
              >
                Mark all read
              </button>
            </div>

            <div className="space-y-3">
            {isLoading && <p className="text-sm text-gray-500">Loading notifications...</p>}

            {!isLoading && !hasNotifications && (
              <p className="text-sm text-gray-500">No notifications yet.</p>
            )}

            {!isLoading &&
              notifications.map((item) => {
                const content = formatNotificationContent(item);
                return (
                  <article
                    key={item.id}
                    className={`rounded-xl border p-3 ${
                      item.is_read ? 'border-gray-200 bg-white' : 'border-[#DEC8E7] bg-[#FBF7FD]'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#F1E7F5] text-base text-[#6D3B7E]" aria-hidden="true">
                        {content.icon}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-semibold text-[#2E2E2E]">{content.title}</h4>
                          {!item.is_read ? <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-[#8A4AA3]" aria-label="Unread" /> : null}
                        </div>
                        <p className="mt-1 break-words text-sm leading-5 text-gray-700">{content.body}</p>
                        {content.detail ? (
                          <p className="mt-2 rounded-lg bg-white/80 px-2.5 py-2 text-xs font-medium leading-5 text-[#4B2A56]">
                            {content.detail}
                          </p>
                        ) : null}
                        {content.reason ? <p className="mt-2 text-xs italic leading-5 text-gray-600">“{content.reason}”</p> : null}
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                          <span>{channelLabel(item.channel)} · {formatDate(item.scheduled_at || item.sent_at)}</span>
                          {!item.is_read ? (
                            <button
                              type="button"
                              className="min-h-9 rounded-lg px-2 font-medium text-[#6D3B7E] hover:bg-[#F1E7F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
                              onClick={() => markAsRead(item.id)}
                            >
                              Mark as read
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="mt-5 border-t pt-4">
            <p className="mb-2 text-sm font-semibold text-[#2E2E2E]">Preferences</p>

            {preferences ? (
              <div className="space-y-2 text-sm text-gray-700">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(preferences.email_enabled)}
                    disabled={isSavingPreferences}
                    onChange={(e) => updatePreference('email_enabled', e.target.checked)}
                  />
                  Email notifications
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(preferences.sms_enabled)}
                    disabled={isSavingPreferences}
                    onChange={(e) => updatePreference('sms_enabled', e.target.checked)}
                  />
                  SMS notifications
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(preferences.in_app_enabled)}
                    disabled={isSavingPreferences}
                    onChange={(e) => updatePreference('in_app_enabled', e.target.checked)}
                  />
                  In-app notifications
                </label>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Loading preferences...</p>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
